import { useEffect, useMemo, useState } from 'react';
import { useDiagnosticDepartments } from '../../services/httpServices/queries';
import { DepartmentPage } from '../clinical/DepartmentPage';
import { FlaskConical, Loader2, AlertCircle, Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export function Administration() {
  const { data, isLoading, error } = useDiagnosticDepartments(1, 100);
  const [selectedId, setSelectedId] = useState<string>('');

  const departments = useMemo(() => data?.data || [], [data]);

  useEffect(() => {
    if (!selectedId && departments.length > 0) {
      setSelectedId(departments[0].id);
    }
  }, [departments, selectedId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic departments</span>
      </div>
    );
  }

  if (departments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Building2 className="w-10 h-10 mb-3 text-text-muted/40" />
        <p className="text-sm font-medium text-text-muted mb-1">No diagnostic departments found</p>
        <p className="text-xs text-text-muted/70">
          Add a department to start managing diagnostic orders.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Department selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-text-muted">
          <FlaskConical className="w-4 h-4" />
          <span className="text-sm font-medium">Departments</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {departments.map((dept: any) => (
            <button
              key={dept.id}
              onClick={() => setSelectedId(dept.id)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                selectedId === dept.id
                  ? 'bg-primary text-white border-primary'
                  : 'bg-surface text-text-muted border-border hover:text-text hover:bg-background',
              )}
            >
              {dept.name}
            </button>
          ))}
        </div>
      </div>

      {selectedId && <DepartmentPage departmentId={selectedId} />}
    </div>
  );
}
